/**
 * Drago Bundle Selector
 * "Make it a kit" add-on: the roller is the base product; an optional
 * checkbox swaps it for the roller + wraps kit bundle product
 * (drago-roller-wraps-kit). Unchecked (default) = roller only.
 * The kit price is fixed per market on the bundle product; no discount code.
 */
class BundleSelector extends HTMLElement {
  constructor() {
    super();
    this.rollerVariant = parseInt(this.dataset.rollerVariant, 10);
    this.kitVariant = parseInt(this.dataset.kitVariant, 10) || null;
    this.cta = this.querySelector('[data-bundle-add]');
    this.buyNow = this.querySelector('[data-bundle-buy-now]');
    this.ctaText = this.querySelector('.bundle-selector__cta-text');
    this.ctaLoading = this.querySelector('.bundle-selector__cta-loading');
    this.errorEl = this.querySelector('[data-bundle-error]');
    this.addonToggle = this.querySelector('.bundle-option__radio');
    this.ctaPrice = this.querySelector('[data-cta-price]');
    // Popup mode (2026-10-22): no tile; the wraps offer opens after Add to Cart / Buy Now.
    this.dialog = this.hasAttribute('data-kit-popup') ? this.querySelector('[data-kit-dialog]') : null;
    this.wrapsVariant = parseInt(this.dataset.wrapsVariant, 10) || null;
    this.wrapsCode = this.dataset.wrapsCode || '';
    this.popupChoice = null;
  }

  connectedCallback() {
    // Default: add-on unchecked -> Add to Cart adds the roller only.
    this.cta.addEventListener('click', () => this.handleAddToCart(false));
    if (this.buyNow) {
      this.buyNow.addEventListener('click', () => this.handleAddToCart(true));
    }
    if (this.addonToggle) {
      this.addonToggle.addEventListener('change', () => this.updateCtaPrice());
    }
    if (this.dialog) {
      if (this.dialog.parentNode !== document.body) document.body.appendChild(this.dialog);
      this.dialog.querySelector('[data-kit-yes]').addEventListener('click', () => this.answerPopup('kit'));
      this.dialog.querySelectorAll('[data-kit-no]').forEach((b) => b.addEventListener('click', () => this.answerPopup('roller')));
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.answerPopup('roller'); });
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.answerPopup('roller'); });
    }
    this.updateCtaPrice();
  }

  updateCtaPrice() {
    const kitOn = this.addonToggle && this.addonToggle.checked;
    const price = kitOn ? this.dataset.priceKit : this.dataset.priceRoller;
    if (this.ctaPrice && price) this.ctaPrice.textContent = price;
    // Sticky bar (moved to <body> by sticky-add-to-cart.js): name and price follow the kit checkbox.
    const sp = document.querySelector('[data-sticky-price]');
    const sn = document.querySelector('[data-sticky-name]');
    if (sp && price) sp.textContent = price;
    if (sn) sn.textContent = kitOn ? 'Roller + Wraps Kit' : 'Drago Roller';
  }

  getSelectedKit() {
    if (this.popupChoice) return this.popupChoice;
    return this.kitVariant && this.addonToggle && this.addonToggle.checked ? 'kit' : 'roller';
  }

  // Ask once per visit. Skipped when the cart already has wraps or the kit,
  // or the shopper already answered this visit.
  async shouldAsk() {
    if (!this.dialog || !this.wrapsVariant) return false;
    try { if (sessionStorage.getItem('drago-kit-popup')) return false; } catch (e) {}
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      this.cartSnapshot = cart;
      if (cart.items.some((i) => i.id === this.kitVariant || i.sku === 'HW' || i.product_id === 8591859122366)) return false;
    } catch (e) {}
    return true;
  }

  // Adds the popup's hidden 50% wraps code to the cart, keeping any code already
  // there (the welcome code, for one). Done before the items are added so the
  // cart drawer renders with the discount.
  async applyWrapsCode() {
    if (!this.wrapsCode) return;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const codes = (cart.discount_codes || []).map((d) => d.code).filter((c) => c && c.toUpperCase() !== this.wrapsCode.toUpperCase());
      codes.push(this.wrapsCode);
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') })
      });
    } catch (e) { /* the wraps still go in at full price */ }
  }

  // Popup Yes in ONE request (was three: read cart, set code, add items).
  // /cart/update.js sets the roller and wraps quantities, adds the 50% code to
  // any codes already on the cart, and returns the drawer sections to render.
  async fastRollerWraps(goToCheckout) {
    const cart = this.cartSnapshot;
    this.cartSnapshot = null;
    const qty = (id) => cart.items.filter((i) => i.id === id).reduce((n, i) => n + i.quantity, 0);
    const rollerNow = qty(this.rollerVariant);
    const updates = {};
    // Buy Now with a roller already in the cart only adds the wraps.
    updates[this.rollerVariant] = goToCheckout && rollerNow > 0 ? rollerNow : rollerNow + 1;
    updates[this.wrapsVariant] = qty(this.wrapsVariant) + 1;
    const codes = (cart.discount_codes || []).map((d) => d.code).filter((c) => c && c.toUpperCase() !== this.wrapsCode.toUpperCase());
    if (this.wrapsCode) codes.push(this.wrapsCode);
    const drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { updates, discount: codes.join(',') };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    try {
      const resp = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body)
      });
      const data = await resp.json();
      if (!resp.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      if (goToCheckout) { window.location.href = '/checkout'; return; }
      if (drawer && typeof drawer.renderContents === 'function') {
        if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
        drawer.renderContents(data);
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.error('Roller + wraps add failed:', e);
      this.showError(e.message || 'Something went wrong. Please try again.');
    } finally {
      if (typeof window.dragoHideCartLoading === 'function') window.dragoHideCartLoading();
      this.setLoading(false);
    }
  }

  answerPopup(choice) {
    if (!this.pending) return;
    try { sessionStorage.setItem('drago-kit-popup', choice); } catch (e) {}
    if (choice === 'kit') choice = 'roller-wraps';
    this.dialog.close();
    // Instant feedback on Yes while the cart updates (Shane: the popup felt slow).
    if (choice === 'kit' && typeof window.dragoShowCartLoading === 'function') window.dragoShowCartLoading({ fullscreen: true });
    const goToCheckout = this.pending.goToCheckout;
    this.pending = null;
    this.popupChoice = choice;
    this.handleAddToCart(goToCheckout, true).finally(() => { this.popupChoice = null; });
  }

  buildItems(kit) {
    switch (kit) {
      case 'roller':
        return [{ id: this.rollerVariant, quantity: 1 }];
      case 'kit':
        return [{ id: this.kitVariant, quantity: 1 }];
      case 'roller-wraps':
        // (the 50% code is put on the cart in applyWrapsCode before this is added)
        // Popup "Yes": roller + wraps as separate lines; the automatic discount halves the wraps.
        return [{ id: this.rollerVariant, quantity: 1 }, { id: this.wrapsVariant, quantity: 1 }];
      default:
        return [{ id: this.rollerVariant, quantity: 1 }];
    }
  }

  setLoading(loading) {
    this.cta.disabled = loading;
    if (this.buyNow) this.buyNow.disabled = loading;
    this.ctaText.hidden = loading;
    this.ctaLoading.hidden = !loading;
  }

  showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  async handleAddToCart(goToCheckout, answered) {
    this.hideError();
    if (!answered && this.dialog) {
      this.setLoading(true);
      const ask = await this.shouldAsk();
      this.setLoading(false);
      if (ask) {
        this.pending = { goToCheckout };
        this.dialog.showModal();
        return;
      }
    }
    this.setLoading(true);

    const kit = this.getSelectedKit();
    if (kit === 'roller-wraps' && this.cartSnapshot) {
      await this.fastRollerWraps(goToCheckout);
      return;
    }
    let items = this.buildItems(kit);
    if (kit === 'roller-wraps') await this.applyWrapsCode();

    // BUY NOW must not stack a second roller. A customer who already added the
    // roller or the kit and then taps Buy Now expects to check out, not add
    // another roller. If the kit is ticked and a plain roller is already in the
    // cart, that roller is swapped for the kit. If nothing is left to add, go
    // straight to checkout.
    if (goToCheckout) {
      try {
        const current = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const hasKit = this.kitVariant && current.items.some((i) => i.id === this.kitVariant);
        const rollerLine = current.items.find((i) => i.id === this.rollerVariant || i.sku === 'DR');
        if (hasKit) {
          items = [];
        } else if (rollerLine) {
          if (kit === 'kit') {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: rollerLine.key, quantity: rollerLine.quantity - 1 })
            });
          } else if (kit === 'roller-wraps') {
            items = [{ id: this.wrapsVariant, quantity: 1 }];
          } else {
            items = [];
          }
        }
      } catch (e) {
        // If the cart lookup fails, fall back to the original add behaviour.
      }
      if (!items.length) {
        window.location.href = '/checkout';
        return;
      }
    }

    // Use the theme's cart drawer for rendering (same pattern as product-form.js)
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const cartAddUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;

    try {
      // Single POST: add the roller or the kit bundle in one request.
      const body = { items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      const resp = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body: JSON.stringify(body)
      });
      const finalData = await resp.json();
      if (!resp.ok || finalData.status) {
        throw new Error(finalData.description || finalData.message || 'Could not add kit to cart');
      }

      if (goToCheckout) {
        window.location.href = '/checkout';
        return;
      }

      // Let the theme's cart drawer handle the render (proper pattern)
      if (cart && typeof cart.renderContents === 'function') {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(finalData);
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.error('Bundle add to cart failed:', e);
      this.showError(e.message || 'Something went wrong. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('bundle-selector')) {
  customElements.define('bundle-selector', BundleSelector);
}

// cachebust:20261022-wraps-fast
